import React from 'react';
import { Accordion, AccordionItem, InputField, HierarchyPicker, TreePickerCategory, TreePickerOption } from '@inmoment/the-kitchen';
import { getLinkTo } from '../../utils/fns';
import styled from 'styled-components';
import { DebugServicePlayground } from '../automated-pages-utils/DebugServicePlayground/DebugServicePlayground';
import { ErrorBoundary } from '../../utils/ErrorBoundary';

type HierarchyPickerPropsAndEvents = Parameters<typeof HierarchyPicker>[0];

const SpacedChildren = styled.div<{ gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap = 48 }) => gap}px;
`;

const updateCategory = (
  categories: TreePickerCategory[],
  categoryId: string,
  updater: (category: TreePickerCategory) => TreePickerCategory,
): TreePickerCategory[] => {
  return categories.map((c) => {
    if (c.id === categoryId) {
      return updater(c);
    }
    return {
      ...c,
      ...(c.subcategories && {
        subcategories: updateCategory(c.subcategories, categoryId, updater),
      }),
    };
  });
};

export const HierarchyPickerDocumentation = (): JSX.Element => {
  const [lazyLoadingHierarchyPickerWrapperElement, setLazyLoadingHierarchyPickerWrapperElement] = React.useState<HTMLDivElement | null>(null);

  const [lazyLoadDurationMs, setLazyLoadDurationMs] = React.useState(1000);
  const [categoriesBatchSize, setCategoriesBatchSize] = React.useState(25);
  const [categoriesCount, setCategoriesCount] = React.useState(100);
  const [optionsBatchSize, setOptionsBatchSize] = React.useState(25);
  const [optionsCount, setOptionsCount] = React.useState(100);

  const completelyLoadedIds = React.useRef<Map<string, { categories?: boolean; options?: boolean }>>(new Map());
  const loadedIds = React.useRef<Set<string>>(new Set());

  const fakeNetworkLatency = () => new Promise<void>((resolve) => setTimeout(resolve, lazyLoadDurationMs));

  const generateOption = (prefix: string, index: number): TreePickerOption => {
    const title = `option ${index}`;
    return {
      id: prefix.length > 0 ? `${prefix} ${title}` : title,
      title: title,
    };
  };

  const generateOptions = (prefix: string, startIndex = 0): TreePickerOption[] => {
    return new Array(optionsBatchSize).fill(null).map((_, i) => generateOption(prefix, startIndex + i));
  };

  const generateCategory = (prefix: string, index: number): TreePickerCategory => {
    const title = `category ${index}`;
    return {
      id: prefix.length > 0 ? `${prefix} ${title}` : title,
      title: title,
      options: [],
      subcategories: [],
    };
  };

  const generateCategories = (prefix = '', startIndex = 0): TreePickerCategory[] => {
    return new Array(categoriesBatchSize).fill(null).map((_, i) => generateCategory(prefix, startIndex + i));
  };

  const [props, setProps] = React.useState<Readonly<HierarchyPickerPropsAndEvents>>({
    categories: [],
    categoryIsFetchingDataMap: {},
    endOfScrollOffset: 10,
    isFetchingData: false,
    onExpansionChange: async ({ id, isExpanded }) => {
      if (id && isExpanded && !loadedIds.current.has(id)) {
        loadedIds.current.add(id);

        setProps((props) => ({
          ...props,
          categoryIsFetchingDataMap: {
            ...props.categoryIsFetchingDataMap,
            [id]: {
              ...props.categoryIsFetchingDataMap?.[id],
              categories: true,
            },
          },
        }));

        await fakeNetworkLatency();

        setProps((props) => ({
          ...props,
          categories: updateCategory(props.categories, id, (c) => {
            if (c.options.length > 0 && c.subcategories !== undefined && c.subcategories.length > 0) {
              return c;
            }
            return {
              ...c,
              options: generateOptions(c.id!),
              subcategories: generateCategories(c.id!),
            };
          }),
        }));

        setProps((props) => ({
          ...props,
          categoryIsFetchingDataMap: {
            ...props.categoryIsFetchingDataMap,
            [id]: {
              ...props.categoryIsFetchingDataMap?.[id],
              categories: false,
            },
          },
        }));
      }
    },
    onSearchChange: async () => {
      let categories: TreePickerCategory[];
      setProps((props) => {
        categories = props.categories;
        return { ...props, isFetchingData: true, categories: [] };
      });

      await fakeNetworkLatency();

      setProps((props) => ({ ...props, isFetchingData: false, categories }));
    },
    onEndOfScroll: async ({ path, type }) => {
      if (type === 'option') {
        const [, categoryId] = path;

        // All options are loaded
        if (completelyLoadedIds.current.get(categoryId)?.options) {
          return;
        }

        setProps((props) => ({
          ...props,
          categoryIsFetchingDataMap: {
            ...props.categoryIsFetchingDataMap,
            [categoryId]: {
              ...props.categoryIsFetchingDataMap?.[categoryId],
              options: true,
            },
          },
        }));

        await fakeNetworkLatency();
        setProps((props) => ({
          ...props,
          categories: updateCategory(props.categories, categoryId, (c) => {
            if (c.options.length >= optionsCount) {
              if (c.id) {
                // This is bad React practice
                completelyLoadedIds.current.set(c.id, { ...completelyLoadedIds.current.get(c.id), options: true });
              }
              return c;
            }

            return {
              ...c,
              options: [...c.options, ...generateOptions(c.id!, c.options.length)],
            };
          }),
        }));

        setProps((props) => ({
          ...props,
          categoryIsFetchingDataMap: {
            ...props.categoryIsFetchingDataMap,
            [categoryId]: {
              ...props.categoryIsFetchingDataMap?.[categoryId],
              options: false,
            },
          },
        }));
      } else if (path.length === 1) {
        // All categories are loaded
        if (propsRef.current.categories.length >= categoriesCount) {
          return;
        }

        setProps((props) => ({ ...props, isFetchingData: true }));

        await fakeNetworkLatency();
        setProps((props) => ({
          ...props,
          categories:
            props.categories.length >= categoriesCount ? props.categories : [...props.categories, ...generateCategories('', props.categories.length)],
        }));

        setProps((props) => ({ ...props, isFetchingData: false }));
      } else {
        const [, parentCategoryId] = path;

        // All subcategories are loaded
        if (completelyLoadedIds.current.get(parentCategoryId)?.categories) {
          return;
        }

        setProps((props) => ({
          ...props,
          categoryIsFetchingDataMap: {
            ...props.categoryIsFetchingDataMap,
            [parentCategoryId]: {
              ...props.categoryIsFetchingDataMap?.[parentCategoryId],
              categories: true,
            },
          },
        }));

        await fakeNetworkLatency();
        setProps((props) => ({
          ...props,
          categories: updateCategory(props.categories, parentCategoryId, (c) => {
            if (c.subcategories!.length >= categoriesCount) {
              if (c.id) {
                // This is bad React practice
                completelyLoadedIds.current.set(c.id, { ...completelyLoadedIds.current.get(c.id), categories: true });
              }
              return c;
            }

            return {
              ...c,
              subcategories: [...c.subcategories!, ...generateCategories(c.id!, c.subcategories!.length)],
            };
          }),
        }));

        setProps((props) => ({
          ...props,
          categoryIsFetchingDataMap: {
            ...props.categoryIsFetchingDataMap,
            [parentCategoryId]: {
              ...props.categoryIsFetchingDataMap?.[parentCategoryId],
              categories: false,
            },
          },
        }));
      }
    },
    onOpenChange: async () => {
      if (props.categories.length === 0) {
        setProps((props) => ({ ...props, isFetchingData: true }));

        await fakeNetworkLatency();
        setProps((props) => ({ ...props, categories: generateCategories() }));

        setProps((props) => ({ ...props, isFetchingData: false }));
      }
    },
  });

  const propsRef = React.useRef(props);
  propsRef.current = props;

  return (
    <SpacedChildren gap={12}>
      <p className="kxi-text-600 kxi-text-normal">Additional HierarchyPicker Documentation</p>

      <Accordion>
        <AccordionItem title="Lazy loading example" open>
          <ErrorBoundary>
            <SpacedChildren>
              <SpacedChildren gap={12}>
                <p>
                  Lazy loading for the HierarchyPicker is almost identical to that of the {getLinkTo('TreePicker')}, and all of its documentation
                  applies. There is only one additional event: "onOpenChange" (see {getLinkTo('HierarchyPickerEvents')}).
                </p>

                <p>
                  Below is an almost* fully functional example implementation of lazy loading with a HierarchyPicker. Use the following controls to
                  modify the lazy loading behavior. This example is modified from the{' '}
                  <a href="https://git.inmoment.com/projects/GRG/repos/the-kitchen/browse/apps/sample/angular/general-industries/src/app/lazy-load/lazy-load.component.ts">
                    LazyLoadComponent
                  </a>{' '}
                  in the Kitchen repo.
                </p>
                <p className="kxi-text-small" style={{ position: 'relative', left: 24 }}>
                  *Searching does not actually modify the data. It just emulates network latency and then provides the same data again, letting the
                  picker handle searching. An actual lazy search would likely send a request with the search query to an API that returns matching
                  data.
                </p>

                <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gridTemplateColumns: 'auto 1fr', columnGap: 6, rowGap: 12 }}>
                  <abbr
                    style={{ alignSelf: 'center' }}
                    title="The amount of time, in milliseconds, the loading animation should appear when loading data"
                  >
                    lazyLoadDurationMs
                  </abbr>
                  <InputField type="number" value={`${lazyLoadDurationMs}`} onChange={(e) => setLazyLoadDurationMs(Number(e.target.value))} />

                  <abbr style={{ alignSelf: 'center' }} title="The number of categories to load each time a load is triggered">
                    categoriesBatchSize
                  </abbr>
                  <InputField type="number" value={`${categoriesBatchSize}`} onChange={(e) => setCategoriesBatchSize(Number(e.target.value))} />

                  <abbr style={{ alignSelf: 'center' }} title="The total number of categories to load">
                    categoriesCount
                  </abbr>
                  <InputField type="number" value={`${categoriesCount}`} onChange={(e) => setCategoriesCount(Number(e.target.value))} />

                  <abbr style={{ alignSelf: 'center' }} title="The number of options to load each time a load is triggered">
                    optionsBatchSize
                  </abbr>
                  <InputField type="number" value={`${optionsBatchSize}`} onChange={(e) => setOptionsBatchSize(Number(e.target.value))} />

                  <abbr style={{ alignSelf: 'center' }} title="The total number of options to load">
                    optionsCount
                  </abbr>
                  <InputField type="number" value={`${optionsCount}`} onChange={(e) => setOptionsCount(Number(e.target.value))} />

                  <abbr style={{ alignSelf: 'center' }} title="Directly sets the endOfScrollOffset prop">
                    endOfScrollOffset
                  </abbr>
                  <InputField
                    type="number"
                    value={`${props.endOfScrollOffset}`}
                    onChange={(e) => setProps((props) => ({ ...props, endOfScrollOffset: Number(e.target.value) }))}
                  />
                </div>

                <SpacedChildren gap={6} ref={setLazyLoadingHierarchyPickerWrapperElement}>
                  <DebugServicePlayground componentName="HierarchyPicker" componentWrapperElement={lazyLoadingHierarchyPickerWrapperElement} />

                  <HierarchyPicker {...props} />
                </SpacedChildren>
              </SpacedChildren>
            </SpacedChildren>
          </ErrorBoundary>
        </AccordionItem>
      </Accordion>
    </SpacedChildren>
  );
};
